/**
 * PropertyRegistry.js — shared property / venue spatial registry
 *
 * Maps property_code strings (Phase 1 FK, mirrors ar_nodes.property_code) to
 * geographic metadata used by:
 *   • CityMapView.jsx     — clickable venue label chips (↗ to focus property)
 *   • PropertyMapView.jsx — flyTo + AR node filter + breadcrumb chip
 *
 * ── Schema ─────────────────────────────────────────────────────────────────
 *   lat/lng     — venue center (Leaflet [lat, lng] / MapLibre [lng, lat])
 *   zoom        — default zoom level when flying to this property
 *   bbox        — ±N-meter bounding box used to filter sessions + AR nodes
 *   label       — human-readable venue name shown in chips and breadcrumbs
 *   venueType   — drives Phase C Cesium / Phase D floor-plan strategy (see below)
 *   cityCode    — parent city key matching CITY_CENTERS in CityMapView.jsx
 *   emoji       — single emoji shown in breadcrumb chip and city-map label
 *   parentCode  — (optional) parent property_code for sub-venues / service points
 *   status      — (optional) "planned" | "active" | "seasonal"; omit = active
 *   opens       — (optional) planned opening year for future venues
 *   services    — (optional) { valet?, rideshare? } with {lat, lng, label} for
 *                  each ground-level service point (becomes a PhysicalSenseAnchor
 *                  of type CURBSIDE in Phase D)
 *
 * ── venueType → Phase C/D rendering strategy ─────────────────────────────
 *   "airport"     → MapLibre vector + indoor SVG path (Phase D)
 *   "arena"       → Cesium ion 3D tile (Sphere / Allegiant / T-Mobile)
 *   "stadium"     → Cesium ion 3D tile (MLB ballpark)
 *   "hotel"       → MapLibre vector + SVG lobby (Phase D)
 *   "expo"        → MapLibre vector + SVG floor plan (Phase D)
 *   "convention"  → MapLibre vector + SVG hall map (Phase D)
 *   "innovation"  → MapLibre vector + SVG floor plan (office/research park)
 *   "car_rental"  → MapLibre vector (surface-level, no indoor needed)
 *   "golf"        → MapLibre vector + outdoor bay map
 *
 * ── property_code → NGSI-LD URN mapping (Phase 2B) ───────────────────────
 *   "JFK_T5"    → urn:ngsi-ld:AirportProperty:JFK:T5
 *   "BOS_TC"    → urn:ngsi-ld:AirportProperty:BOS:TC
 *   "LAS_T3"    → urn:ngsi-ld:AirportProperty:LAS:T3
 *   "ATL"       → urn:ngsi-ld:AirportProperty:ATL
 *   "ATL_DELTA_S" → urn:ngsi-ld:AirportProperty:ATL:DELTA_S
 *   "ATL_INTL"  → urn:ngsi-ld:AirportProperty:ATL:INTL
 *   "LAX"       → urn:ngsi-ld:AirportProperty:LAX
 *   "LAX_TBIT"  → urn:ngsi-ld:AirportProperty:LAX:TBIT
 *   …venue codes → urn:ngsi-ld:AirportProperty:<CODE>
 *
 * ── City coverage ─────────────────────────────────────────────────────────
 *   "JFK"  New York — JFK T5, National Car Rental
 *   "BOS"  Boston  — BOS TC, National Car Rental
 *   "LAS"  Las Vegas — Harry Reid T3, Strip venues, Allegiant, Sphere, T-Mobile,
 *                      MGM, Venetian Expo, Venetian Hotel, Top Golf, Caesars,
 *                      LVCC, Black Fire Innovation, Athletics Ballpark (2028),
 *                      National Car Rental
 *   "ATL"  Atlanta  — ATL Domestic South, Delta Sky Club, International Terminal,
 *                      South Parking, National Car Rental
 *   "LAX"  Los Angeles — LAX Main, Tom Bradley International, National Car Rental
 */

// ── bbox helper: build a ±distanceM-meter box around a center ──────────────
// Used to pre-compute bbox values (1° lat ≈ 111km; 1° lng varies with cos(lat)).
// bbox values below are pre-computed; this comment documents how they were derived.

export const PROPERTY_REGISTRY = {

  // ═══════════════════════════════════════════════════════════════════════════
  // NEW YORK — JFK
  // ═══════════════════════════════════════════════════════════════════════════

  JFK_T5: {
    lat:       40.6432,
    lng:       -73.7858,
    zoom:      18,
    bbox:      { minLat: 40.6400, maxLat: 40.6465, minLng: -73.7910, maxLng: -73.7810 },
    label:     "JFK T5 — JetBlue",
    venueType: "airport",
    cityCode:  "JFK",
    emoji:     "🛫",
    services: {
      rideshare: { lat: 40.6415, lng: -73.7887, label: "JFK T5 Curbside Rideshare Pickup — Level 1" },
      valet:     { lat: 40.6415, lng: -73.7887, label: "JFK T5 Curbside Drop-off — Arrivals Level" },
    },
  },

  /** National Car Rental — JFK (Federal Circle CRCF, AirTrain accessible) */
  NAT_JFK: {
    lat:        40.6501,
    lng:        -73.7939,
    zoom:       16,
    bbox:       { minLat: 40.6480, maxLat: 40.6525, minLng: -73.7975, maxLng: -73.7900 },
    label:      "National Car Rental — JFK",
    venueType:  "car_rental",
    cityCode:   "JFK",
    parentCode: "JFK_T5",
    emoji:      "🚗",
    services: {
      rideshare: { lat: 40.6501, lng: -73.7939, label: "National JFK — AirTrain Federal Circle Exit" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // BOSTON — BOS
  // ═══════════════════════════════════════════════════════════════════════════

  BOS_TC: {
    lat:       42.3636,
    lng:       -71.0096,
    zoom:      18,
    bbox:      { minLat: 42.3595, maxLat: 42.3680, minLng: -71.0170, maxLng: -71.0025 },
    label:     "BOS Terminal C",
    venueType: "airport",
    cityCode:  "BOS",
    emoji:     "🛫",
    services: {
      rideshare: { lat: 42.3629, lng: -71.0083, label: "BOS TC Rideshare Pickup — Arrivals Level" },
      valet:     { lat: 42.3636, lng: -71.0096, label: "BOS TC Curbside Valet / Drop-off" },
    },
  },

  /** National Car Rental — BOS (Consolidated Rental Car Facility, 10 Transportation Way) */
  NAT_BOS: {
    lat:        42.3629,
    lng:        -71.0147,
    zoom:       16,
    bbox:       { minLat: 42.3605, maxLat: 42.3650, minLng: -71.0185, maxLng: -71.0110 },
    label:      "National Car Rental — BOS",
    venueType:  "car_rental",
    cityCode:   "BOS",
    parentCode: "BOS_TC",
    emoji:      "🚗",
    services: {
      rideshare: { lat: 42.3629, lng: -71.0147, label: "National BOS — Car Rental Shuttle Stop" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // LAS VEGAS — HARRY REID INTERNATIONAL AIRPORT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Harry Reid International Airport — Terminal 3 (JetBlue home terminal).
   * JetBlue operates from T3, D Gates. Check-in: T3 ticketing & baggage area.
   * Opened 2012 on the east side of the airport campus.
   */
  LAS_T3: {
    lat:       36.0800,
    lng:       -115.1479,
    zoom:      17,
    bbox:      { minLat: 36.0760, maxLat: 36.0842, minLng: -115.1545, maxLng: -115.1415 },
    label:     "Harry Reid Airport — T3 (JetBlue / D Gates)",
    venueType: "airport",
    cityCode:  "LAS",
    emoji:     "🛫",
    services: {
      rideshare: { lat: 36.0788, lng: -115.1490, label: "LAS T3 Ground Transportation — Rideshare" },
      valet:     { lat: 36.0795, lng: -115.1480, label: "LAS T3 Curbside Drop-off / Valet" },
    },
  },

  /** National Car Rental — LAS (Harry Reid CRCF, shuttle from T1/T3) */
  NAT_LAS: {
    lat:        36.0788,
    lng:        -115.1503,
    zoom:       16,
    bbox:       { minLat: 36.0762, maxLat: 36.0815, minLng: -115.1545, maxLng: -115.1460 },
    label:      "National Car Rental — LAS",
    venueType:  "car_rental",
    cityCode:   "LAS",
    parentCode: "LAS_T3",
    emoji:      "🚗",
    services: {
      rideshare: { lat: 36.0788, lng: -115.1503, label: "National LAS — Rental Car Center Shuttle Stop" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // LAS VEGAS — CESIUM 3D TILE VENUES (Phase C)
  // ═══════════════════════════════════════════════════════════════════════════

  SPHERE: {
    lat:       36.1205,
    lng:       -115.1641,
    zoom:      18,
    bbox:      { minLat: 36.1185, maxLat: 36.1230, minLng: -115.1680, maxLng: -115.1600 },
    label:     "The Sphere",
    venueType: "arena",
    cityCode:  "LAS",
    emoji:     "🌐",
    services: {
      rideshare: { lat: 36.1197, lng: -115.1640, label: "Sphere Rideshare Pickup — North Canopy" },
    },
  },

  ALLEGIANT: {
    lat:       36.0905,
    lng:       -115.1833,
    zoom:      17,
    bbox:      { minLat: 36.0870, maxLat: 36.0942, minLng: -115.1885, maxLng: -115.1775 },
    label:     "Allegiant Stadium",
    venueType: "stadium",
    cityCode:  "LAS",
    emoji:     "🏈",
    services: {
      rideshare: { lat: 36.0921, lng: -115.1835, label: "Allegiant North Rideshare / Drop-off" },
      valet:     { lat: 36.0916, lng: -115.1832, label: "Allegiant Stadium VIP Valet — Gate A Approach" },
    },
  },

  TMOBILE: {
    lat:       36.1028,
    lng:       -115.1723,
    zoom:      18,
    bbox:      { minLat: 36.0988, maxLat: 36.1065, minLng: -115.1775, maxLng: -115.1668 },
    label:     "T-Mobile Arena",
    venueType: "arena",
    cityCode:  "LAS",
    emoji:     "🏒",
    services: {
      rideshare: { lat: 36.1028, lng: -115.1712, label: "T-Mobile Arena Rideshare — East Entry" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // LAS VEGAS — MAPLIBRE VECTOR + PHASE D INDOOR FLOOR PLAN
  // ═══════════════════════════════════════════════════════════════════════════

  MGM: {
    lat:       36.1026,
    lng:       -115.1672,
    zoom:      18,
    bbox:      { minLat: 36.0978, maxLat: 36.1080, minLng: -115.1748, maxLng: -115.1598 },
    label:     "MGM Grand Las Vegas",
    venueType: "hotel",
    cityCode:  "LAS",
    emoji:     "🎰",
    services: {
      valet:     { lat: 36.1031, lng: -115.1660, label: "MGM Grand Porte Cochère — Las Vegas Blvd" },
      rideshare: { lat: 36.1026, lng: -115.1655, label: "MGM Grand Rideshare Pickup — East Entrance" },
    },
  },

  /**
   * Top Golf Las Vegas — on the MGM Grand campus (Level 2 parking structure).
   * Walk-in from the casino, or rideshare directly.
   * parentCode: "MGM" — sub-venue of MGM Grand campus.
   */
  TOPGOLF: {
    lat:        36.0989,
    lng:        -115.1720,
    zoom:       18,
    bbox:       { minLat: 36.0968, maxLat: 36.1010, minLng: -115.1755, maxLng: -115.1682 },
    label:      "Top Golf Las Vegas — MGM Campus",
    venueType:  "golf",
    cityCode:   "LAS",
    parentCode: "MGM",
    emoji:      "🏌️",
    services: {
      rideshare: { lat: 36.0989, lng: -115.1720, label: "Top Golf Rideshare / Drop-off — South Entrance" },
      valet:     { lat: 36.0991, lng: -115.1718, label: "Top Golf Valet Parking — Las Vegas Blvd Approach" },
    },
  },

  /**
   * Caesars Palace Las Vegas — hotel, casino, Forum Shops, Colosseum.
   * One of the largest resort complexes on the Strip.
   */
  CAESARS: {
    lat:       36.1162,
    lng:       -115.1745,
    zoom:      18,
    bbox:      { minLat: 36.1120, maxLat: 36.1210, minLng: -115.1810, maxLng: -115.1675 },
    label:     "Caesars Palace Las Vegas",
    venueType: "hotel",
    cityCode:  "LAS",
    emoji:     "🏛",
    services: {
      valet:     { lat: 36.1163, lng: -115.1731, label: "Caesars Palace Valet — Las Vegas Blvd Main Entrance" },
      rideshare: { lat: 36.1158, lng: -115.1728, label: "Caesars Palace Rideshare Pickup — Flamingo Rd Side" },
    },
  },

  VEX: {
    lat:       36.1252,
    lng:       -115.1702,
    zoom:      17,
    bbox:      { minLat: 36.1220, maxLat: 36.1295, minLng: -115.1745, maxLng: -115.1658 },
    label:     "Venetian Expo (Sands Expo)",
    venueType: "expo",
    cityCode:  "LAS",
    emoji:     "🎭",
    services: {
      rideshare: { lat: 36.1248, lng: -115.1685, label: "Venetian Expo Rideshare — Koval Ln Drop-off" },
    },
  },

  /**
   * Venetian Hotel — the resort tower and casino floor, distinct from VEX (expo hall).
   * The Venetian and Palazzo share the same Las Vegas Blvd mega-complex.
   */
  VEN_HTL: {
    lat:       36.1218,
    lng:       -115.1693,
    zoom:      18,
    bbox:      { minLat: 36.1190, maxLat: 36.1255, minLng: -115.1730, maxLng: -115.1655 },
    label:     "The Venetian Las Vegas — Hotel & Casino",
    venueType: "hotel",
    cityCode:  "LAS",
    emoji:     "🛶",
    services: {
      valet:     { lat: 36.1215, lng: -115.1683, label: "Venetian Valet — Las Vegas Blvd Main Entrance" },
      rideshare: { lat: 36.1215, lng: -115.1690, label: "Venetian Rideshare Pickup — Sands Ave Side" },
    },
  },

  LVCC: {
    lat:       36.1357,
    lng:       -115.1523,
    zoom:      17,
    bbox:      { minLat: 36.1320, maxLat: 36.1402, minLng: -115.1575, maxLng: -115.1470 },
    label:     "Las Vegas Convention Center",
    venueType: "convention",
    cityCode:  "LAS",
    emoji:     "🏛",
    services: {
      rideshare: { lat: 36.1362, lng: -115.1515, label: "LVCC Convention Center Loop — Rideshare / Shuttle" },
    },
  },

  /**
   * Black Fire Innovation Center — UNLV Harry Reid Research & Technology Park.
   * Building 1 (HR1), 4th Floor, Suite 400.
   * Address: 8400 W. Sunset Rd., Las Vegas, NV 89113.
   * Southwest Las Vegas Valley, ~12km from the Strip.
   */
  BFI_LV: {
    lat:       36.0712,
    lng:       -115.2190,
    zoom:      18,
    bbox:      { minLat: 36.0685, maxLat: 36.0740, minLng: -115.2235, maxLng: -115.2145 },
    label:     "Black Fire Innovation — UNLV Harry Reid R&T Park",
    venueType: "innovation",
    cityCode:  "LAS",
    emoji:     "🔬",
    services: {
      valet:     { lat: 36.0714, lng: -115.2185, label: "Black Fire Innovation Visitor Parking — Building HR1 West" },
      rideshare: { lat: 36.0710, lng: -115.2188, label: "Black Fire Innovation Rideshare — W. Sunset Rd Entrance" },
    },
  },

  /**
   * Athletics Las Vegas Ballpark — new MLB stadium at the former Tropicana Las Vegas site.
   * Las Vegas Blvd & Tropicana Ave (former Tropicana hotel footprint).
   * Capacity: 30,000–33,000 seats. Scheduled opening: February 2028.
   * status: "planned" — not yet open; registered for future PhysicalSenseAnchor seeding.
   */
  ATH_LV: {
    lat:       36.0979,
    lng:       -115.1733,
    zoom:      17,
    bbox:      { minLat: 36.0945, maxLat: 36.1015, minLng: -115.1790, maxLng: -115.1675 },
    label:     "Athletics Las Vegas Ballpark (MLB 2028)",
    venueType: "stadium",
    cityCode:  "LAS",
    emoji:     "⚾",
    status:    "planned",
    opens:     "2028",
    services: {
      rideshare: { lat: 36.0979, lng: -115.1733, label: "Athletics LV Rideshare Pickup — Las Vegas Blvd Entry (planned)" },
      valet:     { lat: 36.0980, lng: -115.1730, label: "Athletics LV Valet — Tropicana Ave Approach (planned)" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // ATLANTA — HARTSFIELD-JACKSON (ATL)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Hartsfield-Jackson Atlanta International Airport — main hub entry point.
   * World's busiest airport. MARTA rail station in the central atrium between
   * North and South terminals provides rail access from downtown Atlanta.
   */
  ATL: {
    lat:       33.6407,
    lng:       -84.4277,
    zoom:      14,
    bbox:      { minLat: 33.6300, maxLat: 33.6510, minLng: -84.4420, maxLng: -84.4130 },
    label:     "Hartsfield-Jackson Atlanta — ATL",
    venueType: "airport",
    cityCode:  "ATL",
    emoji:     "✈️",
    services: {
      rideshare: { lat: 33.6390, lng: -84.4315, label: "ATL South Terminal Rideshare — Arrivals Level" },
    },
  },

  /**
   * Delta ATL South Terminal — Delta's primary terminal at ATL.
   * Check-in, baggage drop (South Terminal curbside), and departures for
   * Concourses T, A, B, C, D, E (domestic) connected via underground Plane Train.
   * The North Terminal serves all other domestic carriers.
   */
  ATL_DELTA_S: {
    lat:       33.6402,
    lng:       -84.4315,
    zoom:      17,
    bbox:      { minLat: 33.6375, maxLat: 33.6430, minLng: -84.4385, maxLng: -84.4240 },
    label:     "Delta ATL — South Terminal",
    venueType: "airport",
    cityCode:  "ATL",
    emoji:     "🛫",
    services: {
      rideshare: { lat: 33.6392, lng: -84.4315, label: "Delta South Terminal Rideshare — Level 1 Curbside" },
      valet:     { lat: 33.6402, lng: -84.4315, label: "Delta South Terminal Departures Drop-off" },
    },
  },

  /**
   * ATL International Terminal — Concourses E and F (international flights).
   * Connected to the South Terminal via underground Plane Train / tunnels.
   */
  ATL_INTL: {
    lat:       33.6375,
    lng:       -84.4335,
    zoom:      17,
    bbox:      { minLat: 33.6348, maxLat: 33.6402, minLng: -84.4395, maxLng: -84.4275 },
    label:     "ATL International Terminal (Concourse E/F)",
    venueType: "airport",
    cityCode:  "ATL",
    parentCode: "ATL",
    emoji:     "🌍",
    services: {
      rideshare: { lat: 33.6378, lng: -84.4333, label: "ATL International Terminal Rideshare — Arrivals Drive" },
    },
  },

  /**
   * Delta Sky Club — ATL Concourse B (the flagship Sky Club, largest at ATL).
   * Located post-security in Concourse B. Access via Delta Sky Club membership,
   * Reserve card, or day pass. Multiple Sky Clubs throughout ATL concourses.
   * Phase D: register per-concourse Sky Club nodes under parentCode "ATL_DELTA_S".
   */
  ATL_SKYCLUB: {
    lat:        33.6393,
    lng:        -84.4263,
    zoom:       18,
    bbox:       { minLat: 33.6380, maxLat: 33.6408, minLng: -84.4295, maxLng: -84.4232 },
    label:      "Delta Sky Club — ATL Concourse B",
    venueType:  "lounge",
    cityCode:   "ATL",
    parentCode: "ATL_DELTA_S",
    emoji:      "☁️",
  },

  /**
   * ATL South Terminal Parking — CONRAC Hourly/Daily parking garage.
   * Serves Delta South Terminal. MARTA rail provides airport access from
   * the central atrium between North and South terminals (no rideshare fees).
   */
  ATL_PARKING_S: {
    lat:        33.6435,
    lng:        -84.4362,
    zoom:       16,
    bbox:       { minLat: 33.6410, maxLat: 33.6460, minLng: -84.4405, maxLng: -84.4318 },
    label:      "ATL South Terminal Parking (Hourly / Daily)",
    venueType:  "parking",
    cityCode:   "ATL",
    parentCode: "ATL_DELTA_S",
    emoji:      "🅿️",
    services: {
      rideshare: { lat: 33.6435, lng: -84.4362, label: "ATL Parking South — Shuttle to South Terminal" },
    },
  },

  /** National Car Rental — ATL (CONRAC, 3350 Aviation Circle NW) */
  NAT_ATL: {
    lat:        33.6368,
    lng:        -84.4285,
    zoom:       16,
    bbox:       { minLat: 33.6345, maxLat: 33.6392, minLng: -84.4320, maxLng: -84.4250 },
    label:      "National Car Rental — ATL",
    venueType:  "car_rental",
    cityCode:   "ATL",
    parentCode: "ATL",
    emoji:      "🚗",
    services: {
      rideshare: { lat: 33.6368, lng: -84.4285, label: "National ATL — CONRAC Shuttle Stop" },
    },
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // LOS ANGELES — LAX
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Los Angeles International Airport — main terminal complex (Terminals 1–8 + TBIT).
   * The central theme building and departure/arrival drives are the hub center.
   * LAX-it is the consolidated rideshare pickup lot (separate from terminals).
   */
  LAX: {
    lat:       33.9425,
    lng:       -118.4081,
    zoom:      14,
    bbox:      { minLat: 33.9360, maxLat: 33.9490, minLng: -118.4195, maxLng: -118.3960 },
    label:     "Los Angeles International Airport — LAX",
    venueType: "airport",
    cityCode:  "LAX",
    emoji:     "✈️",
    services: {
      rideshare: { lat: 33.9458, lng: -118.3981, label: "LAX-it Rideshare Pickup Lot (Consolidated)" },
    },
  },

  /**
   * Tom Bradley International Terminal (TBIT) — LAX's international hub.
   * Serves most international carriers. Connected to T4 (American) landside.
   * Branded: Terminal B in the new LAX map numbering.
   */
  LAX_TBIT: {
    lat:        33.9427,
    lng:        -118.4059,
    zoom:       17,
    bbox:       { minLat: 33.9398, maxLat: 33.9455, minLng: -118.4110, maxLng: -118.4005 },
    label:      "LAX Tom Bradley International Terminal (TBIT)",
    venueType:  "airport",
    cityCode:   "LAX",
    parentCode: "LAX",
    emoji:      "🌍",
    services: {
      rideshare: { lat: 33.9427, lng: -118.4059, label: "LAX-it Rideshare — TBIT Shuttle Pickup" },
      valet:     { lat: 33.9427, lng: -118.4065, label: "TBIT Curbside Valet / Drop-off — Upper Level" },
    },
  },

  /**
   * National Car Rental — LAX (LAX Consolidated Rental Car Facility).
   * Off-site; accessible via free LAX shuttle from all terminals.
   * Approximate address: 9020 Aviation Blvd, Inglewood, CA 90301.
   */
  NAT_LAX: {
    lat:        33.9416,
    lng:        -118.3939,
    zoom:       16,
    bbox:       { minLat: 33.9390, maxLat: 33.9440, minLng: -118.3980, maxLng: -118.3895 },
    label:      "National Car Rental — LAX",
    venueType:  "car_rental",
    cityCode:   "LAX",
    parentCode: "LAX",
    emoji:      "🚗",
    services: {
      rideshare: { lat: 33.9416, lng: -118.3939, label: "National LAX — CRCF Shuttle Stop" },
    },
  },

};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns property codes that belong to a given city.
 * Used by CityMapView to add clickable venue labels for the current city.
 * Excludes "planned" sub-venues and car-rental / parking sub-venues from the
 * default city-map chip layer (they appear on drill-down via parentCode instead).
 *
 * Set `includeAll: true` to bypass the filter (e.g. for admin management views).
 *
 * @param {string} cityCode — "JFK" | "BOS" | "LAS" | "ATL" | "LAX"
 * @param {{ includeAll?: boolean }} opts
 * @returns {string[]} — array of property_code strings
 */
export function propertiesForCity(cityCode, { includeAll = false } = {}) {
  return Object.entries(PROPERTY_REGISTRY)
    .filter(([, p]) => {
      if (p.cityCode !== cityCode) return false;
      if (includeAll) return true;
      // On the city-map chip layer, skip sub-venues (parentCode set) and
      // purely operational types that don't benefit from a map chip.
      if (p.parentCode) return false;
      if (p.venueType === "car_rental" || p.venueType === "parking") return false;
      return true;
    })
    .map(([code]) => code);
}

/**
 * Returns true if a lat/lng position falls within the property's bbox.
 * Used by PropertyMapView to filter sessions to the currently focused property.
 *
 * @param {string} propertyCode
 * @param {{ lat: number, lng: number }} pos
 * @returns {boolean}
 */
export function inPropertyBbox(propertyCode, pos) {
  if (!propertyCode || !pos) return true;   // no filter when not set
  const prop = PROPERTY_REGISTRY[propertyCode];
  if (!prop || !prop.bbox) return true;
  const { minLat, maxLat, minLng, maxLng } = prop.bbox;
  return pos.lat >= minLat && pos.lat <= maxLat &&
         pos.lng >= minLng && pos.lng <= maxLng;
}

/**
 * Returns sub-venues that list a given code as their parentCode.
 * Useful for drill-down navigation — e.g. propertiesForParent("ATL") returns
 * ATL_DELTA_S, ATL_INTL, ATL_SKYCLUB, ATL_PARKING_S, NAT_ATL.
 *
 * @param {string} parentCode
 * @returns {string[]}
 */
export function propertiesForParent(parentCode) {
  return Object.entries(PROPERTY_REGISTRY)
    .filter(([, p]) => p.parentCode === parentCode)
    .map(([code]) => code);
}

/**
 * Returns all cities that have at least one primary property registered.
 * Drives future city-selector expansion (adding ATL / LAX pills to CityMapView).
 *
 * @returns {string[]} — e.g. ["JFK", "BOS", "LAS", "ATL", "LAX"]
 */
export function registeredCities() {
  return [...new Set(
    Object.values(PROPERTY_REGISTRY).map((p) => p.cityCode)
  )].sort();
}
